import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SubscriptionContract } from '../../contracts/subscription';
import { ProductContract } from '../../contracts/product';

export interface SubscriptionsState {
    subscriptions: {
        byId: {
            [key: string]: SubscriptionContract;
        };
        ids: string[];
    };
    products: ProductContract[];
    environment: 'sandbox' | 'production';
    loading: boolean;
    error: string | null;
}

export const initialSubscriptionsState: SubscriptionsState = {
    subscriptions: {
        byId: {},
        ids: [],
    },
    products: [],
    environment: 'sandbox',
    loading: false,
    error: null,
};

const slice = createSlice({
    name: 'Subscriptions',
    initialState: initialSubscriptionsState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },

        setSubscriptions: (state, action: PayloadAction<SubscriptionContract[]>) => {
            state.subscriptions.byId = {};
            state.subscriptions.ids = [];
            
            // Ensure payload is an array
            const subscriptions = Array.isArray(action.payload) ? action.payload : [];
            
            subscriptions.forEach(sub => {
                state.subscriptions.byId[sub.id] = sub;
                state.subscriptions.ids.push(sub.id);
            });
            
            state.loading = false;
            state.error = null;
        },

        setProducts: (state, action: PayloadAction<ProductContract[]>) => {
            state.products = action.payload;
        },

        addSubscription: (state, action: PayloadAction<SubscriptionContract>) => {
            const sub = action.payload;
            state.subscriptions.byId[sub.id] = sub;
            state.subscriptions.ids.push(sub.id);
        },

        updateSubscription: (state, action: PayloadAction<SubscriptionContract>) => {
            const sub = action.payload;
            if (state.subscriptions.byId[sub.id]) {
                state.subscriptions.byId[sub.id] = sub;
            }
        },

        removeSubscription: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            delete state.subscriptions.byId[id];
            state.subscriptions.ids = state.subscriptions.ids.filter(subId => subId !== id);
        },

        setEnvironment: (state, action: PayloadAction<'sandbox' | 'production'>) => {
            state.environment = action.payload;
            state.subscriptions = { byId: {}, ids: [] }; // Clear old environment data
            state.error = null;
        },
    },
});

export const {
    setLoading,
    setError,
    setSubscriptions,
    setProducts,
    addSubscription,
    updateSubscription,
    removeSubscription,
    setEnvironment,
} = slice.actions;

export default slice.reducer;
